// Visual juice: floating damage numbers + death particle bursts. Pooled and capped so
// a dense horde can't flood them. Purely cosmetic, but driven from the sim so crits
// read correctly (plan §3.4a juice). Spawned via the seeded RNG → stays deterministic.

import { Pool } from '../core/pool';
import type { Rng } from '../core/rng';

const DMGNUM_CAP = 140;
const PARTICLE_CAP = 500;
const BEAM_CAP = 200;
const RING_CAP = 48;

export interface DamageNumber {
  x: number;
  y: number;
  vy: number;
  life: number;
  maxLife: number;
  value: number;
  crit: boolean;
}

export interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  color: number;
}

export type BeamKind = 'lightning' | 'straight' | 'firefly' | 'spore' | 'solar';

/** A short-lived attack connection rendered according to its native visual language. */
export interface Beam {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  life: number;
  maxLife: number;
  color: number;
  kind: BeamKind;
}

/** An expanding shockwave ring (drawn as a growing, fading circle). */
export interface Ring {
  x: number;
  y: number;
  life: number;
  maxLife: number;
  maxRadius: number;
  color: number;
}

const makeDamageNumber = (): DamageNumber => ({ x: 0, y: 0, vy: 0, life: 0, maxLife: 1, value: 0, crit: false });
const makeParticle = (): Particle => ({ x: 0, y: 0, vx: 0, vy: 0, life: 0, maxLife: 1, color: 0xffffff });
const makeBeam = (): Beam => ({
  x1: 0,
  y1: 0,
  x2: 0,
  y2: 0,
  life: 0,
  maxLife: 1,
  color: 0xffffff,
  kind: 'lightning',
});
const makeRing = (): Ring => ({ x: 0, y: 0, life: 0, maxLife: 1, maxRadius: 1, color: 0xffffff });

export const createDamageNumberPool = (): Pool<DamageNumber> => new Pool(DMGNUM_CAP, makeDamageNumber);
export const createParticlePool = (): Pool<Particle> => new Pool(PARTICLE_CAP, makeParticle);
export const createBeamPool = (): Pool<Beam> => new Pool(BEAM_CAP, makeBeam);
export const createRingPool = (): Pool<Ring> => new Pool(RING_CAP, makeRing);

export function spawnRing(pool: Pool<Ring>, x: number, y: number, maxRadius: number, color: number, lifeSec = 0.4): void {
  const r = pool.acquire();
  if (r === null) return;
  r.x = x;
  r.y = y;
  r.maxRadius = maxRadius;
  r.color = color;
  r.life = lifeSec;
  r.maxLife = lifeSec;
}

export function updateRings(pool: Pool<Ring>, dt: number): void {
  for (let i = 0; i < pool.count; i++) {
    pool.items[i].life -= dt;
    if (pool.items[i].life <= 0) {
      pool.releaseAt(i);
      i--;
    }
  }
}

/** A dense ring of particles flying outward (ability cast blast). */
export function spawnNova(
  pool: Pool<Particle>,
  x: number,
  y: number,
  color: number,
  rng: Rng,
  count: number,
  minSpeed: number,
  maxSpeed: number,
): void {
  for (let k = 0; k < count; k++) {
    const p = pool.acquire();
    if (p === null) return;
    const a = (k / count) * Math.PI * 2 + rng.range(-0.18, 0.18);
    const sp = rng.range(minSpeed, maxSpeed);
    p.x = x;
    p.y = y;
    p.vx = Math.cos(a) * sp;
    p.vy = Math.sin(a) * sp;
    p.life = rng.range(0.35, 0.6);
    p.maxLife = p.life;
    p.color = color;
  }
}

/** Particles starting on a ring and rushing inward (implosion - e.g. Black Hole). */
export function spawnImplosion(
  pool: Pool<Particle>,
  x: number,
  y: number,
  color: number,
  rng: Rng,
  count: number,
  radius: number,
): void {
  for (let k = 0; k < count; k++) {
    const p = pool.acquire();
    if (p === null) return;
    const a = (k / count) * Math.PI * 2;
    const sp = rng.range(180, 280);
    p.x = x + Math.cos(a) * radius;
    p.y = y + Math.sin(a) * radius;
    p.vx = -Math.cos(a) * sp;
    p.vy = -Math.sin(a) * sp;
    p.life = rng.range(0.3, 0.5);
    p.maxLife = p.life;
    p.color = color;
  }
}

export function spawnBeam(
  pool: Pool<Beam>,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  color: number,
  kind: BeamKind = 'lightning',
): void {
  const b = pool.acquire();
  if (b === null) return;
  const life =
    kind === 'firefly' ? 0.24
    : kind === 'spore' ? 0.22
    : kind === 'solar' ? 0.18
    : kind === 'lightning' ? 0.175
    : 0.13;
  b.x1 = x1;
  b.y1 = y1;
  b.x2 = x2;
  b.y2 = y2;
  b.life = life;
  b.maxLife = life;
  b.color = color;
  b.kind = kind;
}

export function updateBeams(pool: Pool<Beam>, dt: number): void {
  for (let i = 0; i < pool.count; i++) {
    pool.items[i].life -= dt;
    if (pool.items[i].life <= 0) {
      pool.releaseAt(i);
      i--;
    }
  }
}

export function spawnDamageNumber(pool: Pool<DamageNumber>, x: number, y: number, value: number, crit: boolean): void {
  const n = pool.acquire();
  if (n === null) return;
  n.x = x;
  n.y = y;
  n.vy = crit ? 64 : 46; // rise speed (px/s)
  n.life = crit ? 0.8 : 0.6;
  n.maxLife = n.life;
  n.value = value;
  n.crit = crit;
}

/** Spawn a small burst of particles when an enemy dies. */
export function spawnDeathBurst(pool: Pool<Particle>, x: number, y: number, color: number, rng: Rng, count = 4): void {
  for (let k = 0; k < count; k++) {
    const p = pool.acquire();
    if (p === null) return;
    const a = rng.range(0, Math.PI * 2);
    const sp = rng.range(40, 130);
    p.x = x;
    p.y = y;
    p.vx = Math.cos(a) * sp;
    p.vy = Math.sin(a) * sp;
    p.life = rng.range(0.25, 0.5);
    p.maxLife = p.life;
    p.color = color;
  }
}

export function updateDamageNumbers(pool: Pool<DamageNumber>, dt: number): void {
  for (let i = 0; i < pool.count; i++) {
    const n = pool.items[i];
    n.y -= n.vy * dt;
    n.life -= dt;
    if (n.life <= 0) {
      pool.releaseAt(i);
      i--;
    }
  }
}

export function updateParticles(pool: Pool<Particle>, dt: number): void {
  for (let i = 0; i < pool.count; i++) {
    const p = pool.items[i];
    p.x += p.vx * dt;
    p.y += p.vy * dt;
    p.vx *= 0.9; // drag
    p.vy *= 0.9;
    p.life -= dt;
    if (p.life <= 0) {
      pool.releaseAt(i);
      i--;
    }
  }
}
